import React from 'react'
import { Button, Well, executeCleanDrawerRender, Drawer } from '@pushly/aqe/lib/components'
import moment from 'moment-timezone'
import { getClassNames } from '../../_utils/classnames'
import { AccountDto } from '../../dtos/account.dto'
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons'
import randomstring from 'randomstring'
import { AccountService, AppService } from '../../services/index'
import { AppState } from '../../stores/app'
import { Container } from 'typescript-ioc'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '../../constants'
import { SETTINGS_WELL_FORM_LAYOUT } from '../../theme/constants'
import { getSupportedTimezones } from '../../_utils/moment'
import deepEqual from 'react-fast-compare'
import { simpleFormErrorNotification } from '../../_utils/utils'
import { ConfigProvider, Flex, Form, Select, Skeleton, Space, Switch, Typography } from 'antd'
import TemplatizedTextField from '@pushly/aqe/lib/components/templatized-text-field/templatized-text-field'

interface IAccountDetailsEditor {
    account: AccountDto
    visible: boolean
    onClose: (e: any) => void
    onAccountUpdated: (account: Partial<AccountDto>) => void
}

interface IADEState {
    urlEnabled: boolean
}

class AccountDetailsEditor extends React.Component<IAccountDetailsEditor, IADEState> {
    public state: IADEState = {
        urlEnabled: this.props.account.defaultNotificationLandingUrl ? true : false,
    }

    protected appState: AppState
    protected appService: AppService
    protected accountService: AccountService

    protected formRef = React.createRef<any>()

    public constructor(props: IAccountDetailsEditor) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.accountService = Container.get(AccountService)

        executeCleanDrawerRender(this)
    }

    public render() {
        const defaultTz = this.props.account.timezone ?? moment.tz.guess()
        const tzOptions = getSupportedTimezones()

        return (
            <Drawer
                getContainer={this.appService.getAppContainer}
                className={getClassNames('adf-details-editor')}
                title="Update Organization Details"
                placement="right"
                closable={true}
                onClose={this.handleDrawerClose}
                onSubmit={this.handleSubmit}
                open={this.props.visible}
                width={800}
            >
                <Form
                    {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                    ref={this.formRef}
                    name="details-settings"
                    requiredMark={false}
                    size="small"
                    labelAlign="left"
                    styles={{
                        content: { marginBottom: 12 },
                    }}
                >
                    <Well title="Organization Information" mode="ghost" hideFooter={true} flush>
                        <Form.Item label="Default Time Zone" name="timezone" initialValue={defaultTz}>
                            <Select size="small" showSearch={true} placeholder="Select a time zone">
                                {tzOptions.map((tz) => (
                                    <Select.Option key={tz} value={tz}>
                                        {tz}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            label="Default Landing URL"
                            extra={
                                <Space align="start">
                                    <InfoCircleOutlined className="info-icon" />
                                    <Typography.Paragraph
                                        type="secondary"
                                        className="font-size--s"
                                        style={{ marginBottom: 0 }}
                                    >
                                        Setting a default landing URL will automatically fill this value in the "Landing
                                        URL" field during organization notification creation.
                                    </Typography.Paragraph>
                                </Space>
                            }
                        >
                            <label>
                                <Switch
                                    size="small"
                                    checked={this.state.urlEnabled}
                                    onChange={() => {
                                        this.setState({ urlEnabled: !this.state.urlEnabled })
                                    }}
                                    aria-label="Enable Custom Default Landing URL"
                                />
                                <span className="label-with-component-l">Enable Custom Default Landing URL</span>
                            </label>
                            {this.state.urlEnabled && (
                                <TemplatizedTextField
                                    size="small"
                                    hideEmojis={true}
                                    macroTypes={['domain']}
                                    formItemProps={{
                                        name: 'defaultNotificationLandingUrl',
                                        initialValue: this.props.account.defaultNotificationLandingUrl,
                                    }}
                                />
                            )}
                        </Form.Item>
                    </Well>
                </Form>
            </Drawer>
        )
    }

    protected get account(): AccountDto {
        return this.props.account
    }

    protected getCurrentValues(): Partial<AccountDto> {
        const values: Partial<AccountDto> = {}
        const formValues = this.formRef.current?.getFieldsValue()

        // Account Details
        if (!!formValues.timezone) {
            if (!deepEqual(formValues.timezone, this.account.timezone)) {
                values.timezone = formValues.timezone
            }
        }

        // Landing URL disabled = null, else value entered
        if (!this.state.urlEnabled) {
            formValues.defaultNotificationLandingUrl = null
        }

        if (!deepEqual(formValues.defaultNotificationLandingUrl, this.account.defaultNotificationLandingUrl)) {
            values.defaultNotificationLandingUrl = formValues.defaultNotificationLandingUrl
        }

        return values
    }

    protected handleSubmit = async () => {
        try {
            const mergedValues = this.getCurrentValues()

            const { ok, data } = await this.accountService.patch(this.account.id, mergedValues, true)

            if (ok) {
                this.handleDrawerClose()
                this.props.onAccountUpdated(data)
            }
        } catch (err) {
            simpleFormErrorNotification(err)
        }
    }

    protected handleDrawerClose = async () => {
        this.props.onClose(null)
    }
}

interface IAccountDetailsWell {
    loading?: boolean
    account: AccountDto
    title?: string
    onAccountUpdated: (account: AccountDto) => void
}

interface IADWState {
    showDrawer: boolean
    drawerKey?: string
}

export class AccountDetailsWell extends React.Component<IAccountDetailsWell, IADWState> {
    public state: IADWState = {
        showDrawer: false,
    }

    protected appState: AppState

    public constructor(props: IAccountDetailsWell) {
        super(props)

        this.appState = Container.get(AppState)
    }

    public render() {
        return (
            <Well
                className="nested"
                title="Details"
                hideFooter={true}
                actions={
                    <Button size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                        <EditOutlined />
                        <span>Edit</span>
                    </Button>
                }
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            <Form
                                {...SETTINGS_WELL_FORM_LAYOUT}
                                colon={false}
                                requiredMark={false}
                                labelAlign="left"
                                size="small"
                            >
                                {this.renderInformationWell()}
                            </Form>

                            <AccountDetailsEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                account={this.account}
                                onClose={this.handleDrawerClose}
                                onAccountUpdated={this.props.onAccountUpdated}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected get account(): AccountDto {
        return this.props.account
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }

    protected renderInformationWell() {
        return (
            <Well title="Organization Information" mode="ghost" hideFooter={true} padding="none">
                <Form.Item label="Org Name">
                    <Typography.Text>{this.props.account.name}</Typography.Text>
                </Form.Item>

                <Form.Item label="Default Timezone">
                    <Typography.Text>{this.props.account.timezone || '(Not Set)'}</Typography.Text>
                </Form.Item>

                <Form.Item label="Default Landing URL">
                    <Typography.Text>{this.props.account.defaultNotificationLandingUrl || '(Not Set)'}</Typography.Text>
                </Form.Item>
            </Well>
        )
    }
}
